/**
 * Help page — General help, feature overview, and troubleshooting.
 *
 * Opened by the "Ouvrir l'aide" ribbon button (#help).
 * Separate from #functions which is the custom functions catalog.
 */

import React, { useMemo, useState } from 'react';
import {
  Text,
  Badge,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components';
import { clearHistory, exportHistoryJson, getHistory } from '../store/history.store';

export const HelpPage: React.FC = () => {
  const [entries, setEntries] = useState(() => getHistory());
  const [moduleFilter, setModuleFilter] = useState<'all' | string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'error'>('all');

  const moduleOptions = useMemo(() => {
    const values = Array.from(new Set(entries.map((e) => e.module).filter(Boolean))) as string[];
    return ['all', ...values];
  }, [entries]);

  const typeOptions = useMemo(() => {
    const values = Array.from(new Set(entries.map((e) => e.type)));
    return ['all', ...values];
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((e) => {
      if (moduleFilter !== 'all' && e.module !== moduleFilter) return false;
      if (typeFilter !== 'all' && e.type !== typeFilter) return false;
      if (statusFilter === 'success' && !e.success) return false;
      if (statusFilter === 'error' && e.success) return false;
      return true;
    });
  }, [entries, moduleFilter, typeFilter, statusFilter]);

  const recentEntries = useMemo(() => filteredEntries.slice(0, 12), [filteredEntries]);

  const handleExportHistory = () => {
    const content = exportHistoryJson();
    const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `excel-ai-studio-history-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const toCsvCell = (value: unknown): string => {
    const raw = value == null ? '' : String(value);
    const escaped = raw.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const buildCsv = () => {
    const header = [
      'timestamp',
      'type',
      'module',
      'success',
      'provider',
      'model',
      'durationMs',
      'sheetName',
      'rangeAddress',
      'request',
      'error',
    ];
    const rows = filteredEntries.map((e) => [
      new Date(e.timestamp).toISOString(),
      e.type,
      e.module ?? '',
      e.success ? 'true' : 'false',
      e.provider,
      e.model,
      e.durationMs ?? '',
      e.sheetName ?? '',
      e.rangeAddress ?? '',
      e.request,
      e.error ?? '',
    ]);
    return [header, ...rows].map((row) => row.map(toCsvCell).join(';')).join('\n');
  };

  const handleExportCsv = () => {
    const content = buildCsv();
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `excel-ai-studio-history-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCopyCsv = async () => {
    try {
      await navigator.clipboard.writeText(buildCsv());
    } catch {
      // no-op: clipboard may be blocked in some environments
    }
  };

  const handleClearHistory = () => {
    const updated = clearHistory();
    setEntries(updated);
  };

  return (
  <div>
    <div className="card">
      <Text className="section-title">Aide - Excel AI Studio v0.3.8</Text>
      <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block' }}>
        Excel AI Studio integre l&apos;IA directement dans Excel via un panneau lateral (task pane) et un ruban dedie.
        Les fonctions personnalisees <code>=EXCELAI.*</code> dependent de la compatibilite Office.
      </Text>
    </div>

    <Accordion multiple collapsible defaultOpenItems={['compatibility', 'ribbon', 'quickstart']}>

      {/* ══ Quick start ══ */}
      <AccordionItem value="quickstart">
        <AccordionHeader>Demarrage rapide</AccordionHeader>
        <AccordionPanel>
          <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <li><Text size={200}>Ouvrez <strong>Parametres</strong> depuis le ruban et configurez votre fournisseur IA (OpenAI, Anthropic, DeepSeek ou Ollama) + cle API.</Text></li>
            <li><Text size={200}>Testez la connexion avec le bouton <strong>Tester la connexion</strong>.</Text></li>
            <li><Text size={200}>Utilisez les boutons du ruban pour acceder aux differentes fonctionnalites.</Text></li>
            <li><Text size={200}>Si votre version Office supporte les Custom Functions, tapez <code>=EXCELAI.</code> dans une cellule pour utiliser les fonctions IA.</Text></li>
            <li><Text size={200}>Si <code>#NOM?</code> apparait pour <code>=EXCELAI.*</code>, utilisez les modules via le panneau (Assistant, Formules, Nettoyage, Analyse...).</Text></li>
          </ol>
        </AccordionPanel>
      </AccordionItem>

      {/* ══ Compatibility ══ */}
      <AccordionItem value="compatibility">
        <AccordionHeader>Compatibilite Office (important)</AccordionHeader>
        <AccordionPanel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <TroubleshootEntry
              issue="Office LTSC / versions sans support Custom Functions"
              solution="Le panneau Excel AI Studio fonctionne, mais les formules =EXCELAI.* peuvent rester en #NOM?."
            />
            <TroubleshootEntry
              issue="Microsoft 365 (canal a jour)"
              solution="Le panneau + les fonctions =EXCELAI.* sont disponibles (autocompletion apres =EXCELAI.)."
            />
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              Si vous ne voyez pas l&apos;autocompletion apres <code>=EXCELAI.</code>, ce n&apos;est pas un probleme de cle API:
              c&apos;est generalement une limite de version Office ou de chargement runtime.
            </Text>
          </div>
        </AccordionPanel>
      </AccordionItem>

      {/* ══ Ribbon buttons ══ */}
      <AccordionItem value="ribbon">
        <AccordionHeader>Boutons du ruban</AccordionHeader>
        <AccordionPanel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <HelpEntry name="Ouvrir AI Studio" description="Ouvre l'assistant IA contextuel. Posez des questions, demandez des formules, analysez vos donnees." />
            <HelpEntry name="Generer formule" description="Generez une formule Excel a partir d'une description en langage naturel." />
            <HelpEntry name="Expliquer formule" description="Explique la formule de la cellule active en langage clair." />
            <HelpEntry name="Nettoyer la selection" description="Nettoyez les donnees : espaces, casse, doublons, extraction d'emails/dates/nombres." />
            <HelpEntry name="Analyser le dataset" description="Analyse statistique + IA : types de colonnes, KPIs, graphiques, anomalies, recommandations." />
            <HelpEntry name="Creer graphique IA" description="L'IA suggere les meilleurs graphiques pour vos donnees." />
            <HelpEntry name="Resumer feuille" description="Resume structure du contenu de la selection : tendances, points cles." />
            <HelpEntry name="Traduire selection" description="Traduit les cellules selectionnees vers la langue de votre choix." />
            <HelpEntry name="Fonctions Excel IA (M365)" description="Catalogue des fonctions =EXCELAI.* (Microsoft 365 requis)." />
            <HelpEntry name="Configurer AI" description="Configurez le fournisseur, le modele, la cle API et la langue des reponses." />
            <HelpEntry name="IA Avancee" description="Modules avances : Copilot, analyseur, scenarios et requetes en langage naturel." />
            <HelpEntry name="Audit Classeur IA" description="Analyse intelligente du classeur : dependances, risques et performance." />
          </div>
        </AccordionPanel>
      </AccordionItem>

      {/* ══ Context detection ══ */}
      <AccordionItem value="context">
        <AccordionHeader>Detection intelligente du contexte</AccordionHeader>
        <AccordionPanel>
          <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block' }}>
            Quand vous ouvrez le panneau sans passer par un bouton specifique du ruban,
            Excel AI Studio detecte automatiquement le contexte de votre selection :
          </Text>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <li><Text size={200}>Cellule avec formule → propose <strong>Expliquer</strong></Text></li>
            <li><Text size={200}>Selection multi-lignes/colonnes → propose <strong>Analyser</strong></Text></li>
            <li><Text size={200}>Selection textuelle → propose <strong>Traduire</strong> ou <strong>Nettoyer</strong></Text></li>
            <li><Text size={200}>Aucune selection exploitable → reste sur <strong>Assistant</strong></Text></li>
          </ul>
          <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginTop: 8, fontStyle: 'italic' }}>
            Les boutons du ruban ouvrent toujours directement la vue correspondante,
            la detection automatique n&apos;interfere pas.
          </Text>
        </AccordionPanel>
      </AccordionItem>

      {/* ══ Troubleshooting ══ */}
      <AccordionItem value="troubleshooting">
        <AccordionHeader>Depannage</AccordionHeader>
        <AccordionPanel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <TroubleshootEntry
              issue="Le panneau ne s'ouvre pas"
              solution="Verifiez que le complement est bien installe via Insertion > Mes complements. Fermez et rouvrez Excel si necessaire."
            />
            <TroubleshootEntry
              issue="Les fonctions =EXCELAI.* ne sont pas reconnues"
              solution="Verifiez d'abord la compatibilite Office. Sur certaines editions (ex: LTSC), =EXCELAI.* peut ne pas etre disponible. Le panneau reste utilisable."
            />
            <TroubleshootEntry
              issue="#CONFIG dans une cellule"
              solution="La cle API n'est pas configuree. Ouvrez Parametres et saisissez votre cle API."
            />
            <TroubleshootEntry
              issue="#ERREUR: Backend inaccessible"
              solution="Le serveur backend n'est pas joignable. Verifiez votre connexion reseau et que le serveur est demarre."
            />
            <TroubleshootEntry
              issue="Reponse lente"
              solution="Le temps de reponse depend du fournisseur IA et de la taille des donnees. Essayez un modele plus rapide dans Parametres."
            />
            <TroubleshootEntry
              issue="Je ne trouve pas un onglet en haut du panneau"
              solution="Les onglets secondaires sont regroupes sous le bouton &quot;Plus&quot; pour eviter le defilement horizontal."
            />
          </div>
        </AccordionPanel>
      </AccordionItem>

      {/* ══ Local history ══ */}
      <AccordionItem value="history">
        <AccordionHeader>Historique local des actions IA</AccordionHeader>
        <AccordionPanel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <Badge appearance="outline" color="informative">
              {filteredEntries.length}/{entries.length} entree{entries.length > 1 ? 's' : ''}
            </Badge>
            <button onClick={handleExportHistory} style={{ padding: '4px 10px', cursor: 'pointer' }}>
              Export JSON
            </button>
            <button onClick={handleExportCsv} style={{ padding: '4px 10px', cursor: 'pointer' }}>
              Export CSV
            </button>
            <button onClick={handleCopyCsv} style={{ padding: '4px 10px', cursor: 'pointer' }}>
              Copier CSV
            </button>
            <button onClick={handleClearHistory} style={{ padding: '4px 10px', cursor: 'pointer' }}>
              Vider
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text size={100}>Module</Text>
              <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} style={{ padding: '3px 6px' }}>
                {moduleOptions.map((m) => (
                  <option key={m} value={m}>{m === 'all' ? 'Tous' : m}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text size={100}>Type</Text>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ padding: '3px 6px' }}>
                {typeOptions.map((t) => (
                  <option key={t} value={t}>{t === 'all' ? 'Tous' : t}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text size={100}>Statut</Text>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'error')} style={{ padding: '3px 6px' }}>
                <option value="all">Tous</option>
                <option value="success">Succes</option>
                <option value="error">Erreur</option>
              </select>
            </label>
          </div>
          {recentEntries.length === 0 ? (
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              Aucun historique pour ce filtre.
            </Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {recentEntries.map((e) => (
                <div key={e.id} style={{ padding: '6px 10px', background: 'var(--colorNeutralBackground2)', borderRadius: 4 }}>
                  <Text size={200} weight="semibold" style={{ display: 'block' }}>
                    {new Date(e.timestamp).toLocaleString('fr-FR')} - {e.type} - {e.success ? 'OK' : 'Erreur'}
                  </Text>
                  <Text size={100} style={{ color: 'var(--text-secondary)' }}>
                    {e.module ? `[${e.module}] ` : ''}{e.request}
                    {e.durationMs ? ` (${e.durationMs} ms)` : ''}
                    {e.rangeAddress ? ` - ${e.rangeAddress}` : ''}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  </div>
);
};

// ─── Sub-components ──────────────────────────────────────

const HelpEntry: React.FC<{ name: string; description: string }> = ({ name, description }) => (
  <div style={{ padding: '6px 12px', background: 'var(--colorNeutralBackground2)', borderRadius: 4 }}>
    <Text size={200} weight="semibold" style={{ display: 'block' }}>{name}</Text>
    <Text size={200} style={{ color: 'var(--text-secondary)' }}>{description}</Text>
  </div>
);

const TroubleshootEntry: React.FC<{ issue: string; solution: string }> = ({ issue, solution }) => (
  <div style={{ padding: '6px 12px', background: 'var(--colorNeutralBackground2)', borderRadius: 4 }}>
    <Text size={200} weight="semibold" style={{ display: 'block' }}>
      <Badge size="small" appearance="tint" color="warning" style={{ marginRight: 6 }}>?</Badge>
      {issue}
    </Text>
    <Text size={200} style={{ color: 'var(--text-secondary)' }}>{solution}</Text>
  </div>
);
